import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { DropzoneOptions, DropzoneState, FileError, FileRejection, useDropzone } from 'react-dropzone'
import { Outlet } from 'react-router-dom'

import { datadogRum } from '@datadog/browser-rum'
import { FeatureFlagList } from '@frame/lib/api/common/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { useRootContext } from '@frame/lib/context'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useFeatureEnabled } from '@frame/lib/hooks/useFeatures'
import { useFileUploadStore } from '@frame/lib/stores/useFileUploadStore'
import { useStore } from '@frame/lib/stores/useStore'

import {
  DEFAULT_MAX_FILES,
  DEFAULT_MAX_FILE_SIZE_BYTES,
  ERROR_CODE_MESSAGES,
  IMAGE_FILE_TYPES,
  PDF_FILE_TYPES,
} from '../constants'
import { ErrorCode, FileStatusMap, FileType, FileUploadState } from '../types'
import { DocumentsFlowProvider } from './documents-flow-context'
import { FileUploadContextProvider } from './file-upload-context'
import { getIsUnsupportedImageType } from './file-upload-context/utils'

type DropzoneContextValue = {
  dropzone: DropzoneState
  files: File[]
  errorMessages: string[]
  fileStatusMap: FileStatusMap
  isUploading: boolean
  dragActive: boolean
  showDropzone: boolean
  setShowDropzone: (show: boolean) => void
  handleUploadFiles: (files: File[]) => void
  removeFile: (file: File) => void
  setDragActive: (dragActive: boolean) => void
  setFiles: (files: File[]) => void
  setFileStatusMap: (fileStatusMap: FileStatusMap) => void
  setErrorMessages: (errorMessages: string[]) => void
  resetDropzone: () => void
}

const defaultValue: DropzoneContextValue = {
  dropzone: {} as DropzoneState,
  files: [],
  errorMessages: [],
  fileStatusMap: {},
  isUploading: false,
  dragActive: false,
  showDropzone: false,
  setShowDropzone: () => void {},
  handleUploadFiles: () => void {},
  removeFile: () => void {},
  setDragActive: () => void {},
  setFiles: () => void {},
  setFileStatusMap: () => void {},
  setErrorMessages: () => void {},
  resetDropzone: () => void {},
}

const DropzoneContext = createContext<DropzoneContextValue | null>(null)

export function DropzoneContextProvider({ forceShow = false, children }: { forceShow?: boolean; children: ReactNode }) {
  const { handleEvent } = useEventHandler()
  const { api } = useRootContext()
  const { setUploadedFileIds } = useFileUploadStore(({ setUploadedFileIds }) => ({
    setUploadedFileIds,
  }))

  const [showDropzone, setShowDropzone] = useState(forceShow)
  const [files, setFiles] = useState<File[]>([])
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [fileStatusMap, setFileStatusMap] = useState<FileStatusMap>({})
  const [isUploading, setIsUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const dropZoneConfig: DropzoneOptions = {
    maxSize: DEFAULT_MAX_FILE_SIZE_BYTES,
    maxFiles: DEFAULT_MAX_FILES,
    accept: {
      'image/*': IMAGE_FILE_TYPES,
      'application/pdf': PDF_FILE_TYPES,
    },
    multiple: true,
    noDragEventsBubbling: true,
    noClick: true,
    validator: duplicateFileValidator,
    onDragEnter: () => setDragActive(true),
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setDragActive(false)

      const fileClones = acceptedFiles.map(async (file) => {
        const fileArrayBuffer = await file.arrayBuffer()
        const fileClone = new File([fileArrayBuffer], file.name, { type: file.type })

        return fileClone
      })

      Promise.all(fileClones)
        .then((fileClones) => {
          // Avoid accessing `acceptedFiles` to prevent a Chromium bug.
          // Link: https://bugs.chromium.org/p/chromium/issues/detail?id=1063576
          updateFiles(fileClones)
          handleUploadFiles(fileClones)
        })
        .catch(() => {
          const errors = acceptedFiles.map((file) => `Failed to upload: ${file.name}`)
          setErrorMessages(errors)
        })

      updateErrors([
        ...new Set(
          fileRejections.flatMap(({ file, errors }) =>
            errors.map((e) =>
              (e.code as ErrorCode) === ErrorCode.DUPLICATE_FILE
                ? `${ERROR_CODE_MESSAGES()[e.code as ErrorCode]}: ${file.name}`
                : ERROR_CODE_MESSAGES()[e.code as ErrorCode]
            )
          )
        ),
      ])
    },
  }

  function duplicateFileValidator(file: File): FileError | null {
    const duplicateFile = files.find((f) => f.name === file.name)

    if (duplicateFile) {
      return {
        code: ErrorCode.DUPLICATE_FILE,
        message: ERROR_CODE_MESSAGES()[ErrorCode.DUPLICATE_FILE],
      }
    }

    return null
  }

  function updateFiles(files: File[]) {
    setFiles((prev) => [...prev, ...files])
  }

  function updateErrors(errors: string[]) {
    setErrorMessages(errors)
  }

  function removeFile(file: File) {
    setFiles((files) => files.filter((f) => f !== file))
    setFileStatusMap((statusMap) => {
      const toDelete = Object.values(statusMap).find((f) => f.fileName === file.name)
      toDelete && delete statusMap[toDelete.fileName]
      return statusMap
    })
  }

  async function handleUploadFiles(files: File[]) {
    setIsUploading(true)

    await Promise.all(
      files.map(async (file) => {
        const fileName = file.name.trim()

        setFileStatusMap((s: FileStatusMap) => {
          return {
            ...s,
            [fileName]: { fileName, state: FileUploadState.PROCESSING, error: '', fileId: '' },
          }
        })

        try {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('type', FileType.CONSUMER_DOCUMENT)

          const { error, data } = await api.tasks.uploadFile(formData)

          if (data?.id) {
            setFileStatusMap((s: FileStatusMap) => {
              return {
                ...s,
                [fileName]: { fileName, state: FileUploadState.COMPLETED, error: undefined, fileId: data.id },
              }
            })
            setUploadedFileIds([data.id])
          } else if (error) {
            setFileStatusMap((s: FileStatusMap) => {
              return {
                ...s,
                [fileName]: { fileName, state: FileUploadState.FAILED, error: error?.file?.[0], fileId: '' },
              }
            })

            updateErrors(error?.file ?? [])

            const isUnsupportedImageType = getIsUnsupportedImageType(file)

            if (isUnsupportedImageType) {
              handleEvent(InternalEventType.ATTEMPTED_TO_UPLOAD_UNSUPPORTED_IMAGE_TYPE, {
                internal_metadata: {
                  file_name: fileName,
                  file_type: file.type,
                },
              })
            }
          }
        } catch (err) {
          setFileStatusMap((s: FileStatusMap) => {
            return {
              ...s,
              [fileName]: {
                fileName,
                state: FileUploadState.FAILED,
                error: 'Failed to upload',
                fileId: '',
              },
            }
          })
        }
      })
    )

    handleEvent(InternalEventType.FILE_UPLOADED)

    setIsUploading(false)
  }

  function resetDropzone() {
    setErrorMessages([])
    setFiles([])
    setFileStatusMap({})
    setShowDropzone(false)
  }

  const dropzone = useDropzone(dropZoneConfig)

  return (
    <DropzoneContext.Provider
      value={{
        dropzone,
        files,
        errorMessages,
        fileStatusMap,
        isUploading,
        dragActive,
        showDropzone,
        setShowDropzone,
        handleUploadFiles,
        removeFile,
        setDragActive,
        setFiles,
        setFileStatusMap,
        setErrorMessages,
        resetDropzone,
      }}
    >
      {children}
    </DropzoneContext.Provider>
  )
}

export function DropzoneLayout() {
  const isDocsV2Enabled = useFeatureEnabled(FeatureFlagList.DocumentsV2)

  useEffect(() => {
    datadogRum.addFeatureFlagEvaluation(FeatureFlagList.DocumentsV2, isDocsV2Enabled)
  }, [isDocsV2Enabled])

  const { docsForMortgageEnabled } = useStore(({ session }) => ({
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled ?? false,
  }))

  if (isDocsV2Enabled && !docsForMortgageEnabled) {
    return (
      <DocumentsFlowProvider>
        <FileUploadContextProvider>
          <Outlet />
        </FileUploadContextProvider>
      </DocumentsFlowProvider>
    )
  }

  return (
    <DropzoneContextProvider>
      <Outlet />
    </DropzoneContextProvider>
  )
}

export function useDropzoneContext() {
  const context = useContext(DropzoneContext)

  if (!context) {
    return defaultValue
  }
  return context
}
